import type { CompanyType, DepartureMode } from 'common/models';

export interface CompanyToEdit {
  name: string;
  identificationNumber?: string | null;
  taxNumber?: string | null;
  description?: string | null;
  address: CompanyAddressToUpdate;
}

export interface CompanyAddress {
  street: string;
  city: string;
  country?: string | null;
  postalCode: string;
  latitude: number;
  longitude: number;
  id: string;
  created: string;
  modified: string;
}

export interface CompanyAddressToUpdate {
  street: string;
  city: string;
  country?: string | null;
  postalCode: string;
  /** @format double */
  latitude: number;
  /** @format double */
  longitude: number;
}

export interface SettingsToUpdate {
  timezoneId: string;
  globalSettings?: GlobalCompanySettings | null;
  dailyPlanSettings?: DailyPlanSettings | null;
  orderSettings?: OrderSettings | null;
  customFormsSettings?: CustomFormsSettings | null;
}

export interface CompanySettingsToEdit {
  orderSettings?: OrderSettings | null;
  dailyPlanSettings?: DailyPlanSettings | null;
  globalSettings?: GlobalCompanySettings | null;
}

export type AddressSearchProvider = 'Seznam' | 'Google';
export type OrderRequestFormType = 'CompanyAndHousehold' | 'HouseholdOnly' | 'CompanyOnly';

export const AddressSearchProviderArray: AddressSearchProvider[] = ['Seznam', 'Google'];
export const OrderRequestFormTypeArray: OrderRequestFormType[] = [
  'CompanyAndHousehold',
  'CompanyOnly',
  'HouseholdOnly'
];

export interface GlobalCompanySettings {
  countryId: string;
  currencyId?: string | null;
  languageId?: string | null;
  type: CompanyType;
  hideStores: boolean;
  restrictTechnicianDataVisibility?: boolean;
  requireComplexPasswords?: boolean;
  defaultAddressSearchProvider?: AddressSearchProvider | null;
  customerPortalEnabled: boolean;
  primaryColor?: string | null;
  accentColor?: string | null;
}

export interface DailyPlanSettings {
  /** @format int32 */
  workHoursStart: number;

  /** @format int32 */
  workHoursEnd: number;

  /** @format int32 */
  precision: number;

  /** @format int32 */
  defaultInterventionLength: number;
  noOverlaps: boolean;
}

export interface OrderSettings {
  autoConfirmation?: boolean | null;
  singleAssigneeMode?: boolean | null;
  departureMode: DepartureMode;
  reportEmails: string[];
  orderCodeTemplate?: string | null;
  customerPortalRequestCodeTemplate?: string | null;
  orderRequestCodeTemplate?: string | null;
  /** @format double */
  travelFee: number;
  /** @format double */
  pricePerKilometer: number;
  requireTechnicianLocation?: boolean | null;
  maxTravelFeeMode?: boolean | null;
  disableManualCustomForms?: boolean | null;
  disableManualCustomFormsForTechnician?: boolean | null;
  enableGroupsAndSkills?: boolean | null;
  enableSlotSuggestion?: boolean | null;
  orderRequestFormType?: OrderRequestFormType | null;
  orderRequestFormRequestedDateEnabled?: boolean | null;
  orderRequestFormServiceObjectInfoEnabled?: boolean | null;
  orderRequestInfoPageContent?: string | null;
  requestedDateNote?: string | null;
}

export interface CustomFormsSettings {
  customerCustomDataTemplate: string;
  orderCustomDataTemplate: string;
  interventionBuilderConfig: string;
  interventionProcessorConfig: string;
}

export interface CompanyPreferences {
  timezoneId?: string | null;
  currencyId?: string | null;
  language?: string | null;
  emailSenderEmail?: string | null;
  emailSenderName?: string | null;
  emailReplyTo?: string | null;
  notificationSignature?: string | null;
  allowCustomerNotificationEmails?: boolean | null;
  allowCustomerNotificationSmsMessages?: boolean | null;
  allowCustomerReportEmails?: boolean | null;
  allowCustomerReportSmsMessages?: boolean | null;
  allowUserNotificationEmails?: boolean | null;
  allowUserNotificationSmsMessages?: boolean | null;
  notificationDebugMode?: boolean | null;
  disableSmsDiacritics?: boolean | null;
}
